import { CompanyAvatar } from "@/components/molecules/company-avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUser } from "@/features/auth/hooks/use-user";
import { useLeaveClient } from "@/features/clients/hooks/use-clients";
import type { ClientWithMembership } from "@/features/clients/types";
import { useTranslation } from "@/features/utils/i18n";
import { DotsThreeOutlineIcon, TrashIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export const useCompaniesColumns = (): ColumnDef<ClientWithMembership>[] => {
  const { t } = useTranslation("companies");

  return [
    {
      accessorKey: "client.name",
      header: t("table.company"),
      cell: ({ row }) => {
        const { client } = row.original;

        return (
          <div className="flex items-center gap-3">
            <CompanyAvatar logo={client.logo} name={client.name} size="sm" />
            <div className="font-medium">{client.name}</div>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => {
        return <CompanyActions company={row.original} />;
      },
    },
  ];
};

const CompanyActions = ({ company }: { company: ClientWithMembership }) => {
  const { t } = useTranslation("companies");
  const navigate = useNavigate();
  const { data: user } = useUser();
  const leaveClient = useLeaveClient();

  // Leaving is its own operation, not a revocation aimed at yourself: it needs
  // no permission, and the account never holds its own access id here.
  const handleLeaveCompany = async () => {
    if (!user?.id) return;

    try {
      await leaveClient.mutateAsync(company.client.id);
      toast.success(t("messages.leftCompany"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("messages.leaveCompanyFailed"),
      );
    }
  };

  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/${company.client.id}/home`)}
      >
        {t("actions.open")}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">{t("actions.openMenu")}</span>
            <DotsThreeOutlineIcon weight="fill" className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={handleLeaveCompany}
            className="text-destructive"
          >
            <TrashIcon className="h-4 w-4 mr-2" weight="fill" />
            {t("actions.leaveCompany")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
